import { COMPANIES_API, USERS_API } from 'api';
import { onAuthStateChanged } from 'firebase/auth';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { USER_ROLES } from 'utils/constant';
import { firebaseAuth } from 'utils/firebase';

import AuthContext from './authContext';

const AuthState = ({ children }) => {
	const [currentUser, setCurrentUser] = useState(null);
	const [currentUserAdditionalData, setCurrentUserAdditionalData] = useState(null);
	const [guestUser, setGuestUser] = useState(null);
	const [currentUserRole, setCurrentUserRole] = useState(null);
	const refreshInProgressRef = useRef(false);
	const refreshUserTokenAndRolesCb = useRef(null);
	const refreshUserTokenAndRolesPromise = useRef(null);

	const getUserAdditionalData = useCallback(
		async (uid, cid) => {
			try {
				const promises = [];
				promises.push(USERS_API.getUser(uid));
				if (cid) {
					promises.push(COMPANIES_API.getCompany(cid));
				}
				const results = await Promise.all(promises);
				const userRes = results[0];
				if (userRes) {
					if (cid) {
						// eslint-disable-next-line prefer-destructuring
						userRes.company = results[1];
					}

					setCurrentUserAdditionalData(userRes);
					return userRes;
				}
				return null;
			} catch (error) {
				// eslint-disable-next-line no-console
				console.log(error.message);
				throw error;
			}
		},
		[setCurrentUserAdditionalData]
	);

	const setRoles = useCallback(
		(user, idTokenResult, repeat = 3) => {
			if (idTokenResult.claims.companyId === undefined) {
				if (repeat > 0) {
					setTimeout(
						() =>
							user
								.getIdTokenResult(true)
								.then(_idTokenResult => setRoles(user, _idTokenResult, repeat - 1)),
						3000
					);
				} else {
					// timeout
					refreshInProgressRef.current = false;
					if (refreshUserTokenAndRolesCb.current) {
						refreshUserTokenAndRolesCb.current('Timeout', null);
					}
				}
				// newly created user.. wait for login
				return;
			}
			refreshInProgressRef.current = false;
			setCurrentUser(user);
			getUserAdditionalData(user.uid, idTokenResult.claims.companyId)
				.then(cuaData => {
					if (refreshUserTokenAndRolesCb.current) {
						refreshUserTokenAndRolesCb.current(null, cuaData);
					}
				})
				.catch(e => {
					if (refreshUserTokenAndRolesCb.current) {
						refreshUserTokenAndRolesCb.current(e, null);
					}
				});
			setGuestUser(null);

			if (idTokenResult.claims.admin) {
				setCurrentUserRole(USER_ROLES.admin);
				return;
			}

			if (idTokenResult.claims.companyAdmin) {
				setCurrentUserRole(USER_ROLES.companyAdmin);
			}
		},
		[getUserAdditionalData, setCurrentUser, setCurrentUserRole, setGuestUser]
	);

	useEffect(() => {
		onAuthStateChanged(firebaseAuth, async user => {
			if (user) {
				if (!refreshInProgressRef.current) {
					refreshInProgressRef.current = true;
					user.getIdTokenResult().then(idTokenResult => setRoles(user, idTokenResult, 0));
				}
			} else {
				setCurrentUser(null);
				setCurrentUserAdditionalData(null);
				setGuestUser(null);
				setCurrentUserRole(USER_ROLES.user);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const providerValue = useMemo(() => {
		const refreshUserTokenAndRoles = async user => {
			if (refreshUserTokenAndRolesCb.current) {
				// eslint-disable-next-line no-console
				console.warn('Refresh callback is already set');
				return refreshUserTokenAndRolesPromise.current;
			}

			refreshInProgressRef.current = true;

			refreshUserTokenAndRolesPromise.current = new Promise((resolve, reject) => {
				refreshUserTokenAndRolesCb.current = (err, result) => {
					refreshUserTokenAndRolesCb.current = null;
					refreshUserTokenAndRolesPromise.current = null;
					if (err) reject(err);
					else resolve(result);
				};
			});
			user.getIdTokenResult(true).then(idTokenResult => setRoles(user, idTokenResult));
			return refreshUserTokenAndRolesPromise.current;
		};
		return {
			currentUser,
			setCurrentUser,
			currentUserAdditionalData,
			setCurrentUserAdditionalData,
			guestUser,
			setGuestUser,
			refreshUserTokenAndRoles,
			currentUserRole,
		};
	}, [
		currentUser,
		setCurrentUser,
		currentUserAdditionalData,
		setCurrentUserAdditionalData,
		guestUser,
		setGuestUser,
		setRoles,
		currentUserRole,
	]);

	return <AuthContext.Provider value={providerValue}>{children}</AuthContext.Provider>;
};

export default AuthState;
